import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import axios from "axios";
import { API_DOMAIN } from "../constants";
import { useSocket } from "./SocketContext";

const WatiSocketContext = createContext();

export const useWatiSocket = () => {
  const context = useContext(WatiSocketContext);
  if (!context) {
    throw new Error('useWatiSocket must be used within a WatiSocketProvider');
  }
  return context;
};

export const WatiSocketProvider = ({ children }) => {
  const { socket, isConnected } = useSocket();
  const [watiChatUpdates, setWatiChatUpdates] = useState([]);
  const [relayStatus, setRelayStatus] = useState(null);
  // Consumers use this as the effective live-update path, not merely the
  // backend's upstream state. A browser outage must turn it false so the
  // existing false -> true recovery effects reload missed chat/board data.
  const isWatiConnected = isConnected && relayStatus?.connected === true;

  useEffect(() => {
    if (!socket) {
      setRelayStatus(null);
      return;
    }
    const handleChatUpdate = (data) => {
      setWatiChatUpdates((prev) => [...prev, data]);
    };
    const handleRelayStatus = (payload) => setRelayStatus(payload?.data ?? payload ?? null);
    socket.on("chatUpdate", handleChatUpdate);
    socket.on("wati:relay_status", handleRelayStatus);

    return () => {
      socket.off("chatUpdate", handleChatUpdate);
      socket.off("wati:relay_status", handleRelayStatus);
    };
  }, [socket]);

  useEffect(() => {
    if (!socket || !isConnected) return;
    socket.emit("wati:relay_status:get", (payload) => setRelayStatus(payload?.data ?? payload ?? null));
  }, [socket, isConnected]);

  const reconnect = useCallback(async () => {
    if (!isConnected) return;
    const response = await axios.post(`${API_DOMAIN}/wati_relay/reconnect`);
    setRelayStatus(response.data?.data ?? null);
  }, [isConnected]);

  return (
    <WatiSocketContext.Provider value={{ watiChatUpdates, setWatiChatUpdates, relayStatus, isWatiConnected, reconnect }}>
      {children}
    </WatiSocketContext.Provider>
  );
};
